import { defineComponent as _defineComponent } from 'vue'
import StateChip from '@/components/StateChip.vue';
import TagChip from '@/components/TagChip.vue';
import TextTruncatedChip from '@/components/TextTruncatedChip.vue';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import {
  SdsesFilterParams,
  SdsesFilterParamsKeys,
  SdsesFilterParamsValue,
  UseSdsFilter,
  WORKING_ENV_RECOMMENDATION,
  getRiskLevelChipColor,
  getWorkingEnvRecommendationChipColor,
} from '@/composables/useSdsFilter';
import { UserSearchCriteria } from '@/interfaces/api/general/company';
import { DynamicList } from '@/interfaces/api/general/dynamicList';
import { Department } from '@/interfaces/model/general/department';
import { buildDepartmentOptions } from '@/utils/departmentTree';
import { Tag } from '@/interfaces/model/general/tag';
import { User } from '@/interfaces/vuex/user';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { NO_TAG_ID, NO_DEPARTMENT_ID } from '@/lib/constant';
import { i18n } from '@/plugins/vue-i18n';
import SaveMyFilterDialog from '@/views/pages/sdsIndex/SaveMyFilterDialog.vue';
import { computed, ref, UnwrapRef } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsCurrentFilter',
  props: {
    companyUsers: null,
    tags: null,
    status: null,
    departments: null,
    dynamicLists: null,
    currentFilterParams: null,
    resetFilters: null
  },
  emits: ["update", "created:myFilter"],
  setup(__props: any, { emit: emits }: { emit: ({
  (
    e: 'update',
    key: SdsesFilterParamsKeys,
    value: SdsesFilterParamsValue
  ): void;
  (e: 'created:myFilter', value: UserSearchCriteria): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  companyUsers: User[];
  tags: Tag[];
  status: UnwrapRef<UseSdsFilter['statusOptions']>;
  departments: Department[];
  dynamicLists: DynamicList[];
  currentFilterParams: SdsesFilterParams;
  resetFilters;
};





const $t = i18n.t.bind(i18n);
const noTagChip: Tag = {
  id: NO_TAG_ID,
  name: String($t(sharedMessages.noTag)),
  color: 0,
};

const showSaveMyFilterDialog = ref(false);
const m = defineMessages({
  saveToMyFilter: 'マイフィルターに保存する',
});

const { isFeatureActive, COMPANY_FEATURES, generateInactiveMessage } =
  useCompanyFeatureComposable();

const handleDeleteFilterFromArrayParam = (
  key: SdsesFilterParamsKeys,
  value: string | number
) => {
  const array = props.currentFilterParams[key];
  if (!Array.isArray(array)) {
    throw new Error(`value is not array. key:${key}`);
  }

  emits(
    'update',
    key,
    array.filter((v) => v !== value)
  );
};

const handleDeleteFilterParam = (key: SdsesFilterParamsKeys) => {
  emits('update', key, undefined);
};

const getUserName = (userId: string): string => {
  const user = props.companyUsers.find((user) => user.uuid === userId);

  if (!user) {
    return '';
  }

  return `${user.last_name || 'Unknown'} ${user.first_name || ''}`;
};

const statusChipClass = (stateValue: string): string => {
  const state = props.status.find((state) => state.value === stateValue);
  return state ? `${state.state} mr-2` : 'mr-2';
};

// 適用中の絞り込みもフルパスで出す。親が違えば同名の部署を作れるため、
// 名前だけだとどの部署で絞り込んでいるのか分からない。
const departmentPaths = computed(
  () =>
    new Map(
      buildDepartmentOptions(props.departments).map((option) => [
        option.uuid,
        option.path,
      ])
    )
);

const getDepartmentName = (departmentId: string | number): string => {
  if (departmentId === NO_DEPARTMENT_ID)
    return String($t(sharedMessages.noDepartmentSDS));
  return departmentPaths.value.get(String(departmentId)) ?? '';
};

const getDynamicListName = (dynamicListId: number) =>
  props.dynamicLists.find((dynamicList) => dynamicList.id === dynamicListId)
    ?.name ?? '';

const workingEnvRecommendationLabel = (value: string | number): string => {
  switch (value) {
    case WORKING_ENV_RECOMMENDATION.RECOMMENDED:
      return String(
        $t(sharedMessages.riskAssessmentWorkingEnvInputRecommended)
      );
    case WORKING_ENV_RECOMMENDATION.COMPLETED:
      return String($t(sharedMessages.riskAssessmentWorkingEnvInputCompleted));
    case WORKING_ENV_RECOMMENDATION.NOT_RECOMMENDED:
      return String(
        $t(
          sharedMessages.riskAssessmentWorkingEnvInputNotRecommendedFilterOption
        )
      );
    default:
      return '-';
  }
};

return { __sfc: true,props, emits, $t, noTagChip, showSaveMyFilterDialog, m, isFeatureActive, COMPANY_FEATURES, generateInactiveMessage, handleDeleteFilterFromArrayParam, handleDeleteFilterParam, getUserName, statusChipClass, departmentPaths, getDepartmentName, getDynamicListName, workingEnvRecommendationLabel, StateChip, TagChip, TextTruncatedChip, getRiskLevelChipColor, getWorkingEnvRecommendationChipColor, sharedMessages, SaveMyFilterDialog }
}

})